import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Boxes, History, Layers, MoveRight, Printer, ScanLine, Tag } from 'lucide-react';
import {
  type CellCardDto,
  type StockPlaceDto,
  type Unit,
} from '@eombor/contracts';
import { Badge, Button, EmptyState, Skeleton, cn } from '@eombor/ui';
import { ApiClientError } from '../../../shared/api/client.js';
import { t } from '../../../shared/i18n/index.js';
import { usePermission } from '../../auth/model/usePermission.js';
import { BatchHistoryDialog } from '../../stock/ui/BatchHistoryDialog.js';
import { MoveStockDialog, type MoveStockTarget } from '../../stock/ui/MoveStockDialog.js';
import { useStockByCell } from '../../stock/model/stock.queries.js';
import { warehouseApi } from '../api/warehouse.api.js';
import { cellStatusLabel, cellStatusTone } from '../model/cell-status.js';
import { LabelPrintDialog } from './LabelPrintDialog.js';
import {
  BatchLabelDialog,
  type BatchLabelTarget,
} from '../../stock/ui/BatchLabelDialog.js';
import { unitText } from '../../../shared/i18n/labels.js';

export interface CellCardPageProps {
  warehouseId: string;
  /** Etiketkadagi kod — manzildan keladi, ya'ni sahifa xatcho'pga olinadi. */
  code: string;
  /** Ombor chizmasiga qaytish. Sahifa marshrutlashni bilmaydi. */
  onBack: () => void;
  /** Shu katakni ombor chizmasida ochish. */
  onShowOnLayout: (cellId: string) => void;
}

interface HistoryTarget {
  batchId: string;
  batchNo: string;
  unit: Unit;
}

/**
 * To'lganlik chizig'i.
 *
 * Sig'im o'lchanmagan bo'lsa (`null`) chiziq umuman chizilmaydi — nol foizli
 * chiziq "bo'sh" degan MA'NO beradi va bu yolg'on bo'lardi: sig'imi noma'lum
 * katak to'la ham bo'lishi mumkin.
 */
const FillBar = ({ percent }: { percent: number }) => (
  <div className="flex flex-col gap-1.5">
    <div className="flex items-baseline justify-between gap-2">
      <span className="text-xs text-text-faint">{t('warehouse.cardFill')}</span>
      <span className="tabular text-sm font-medium text-text">{`${percent}%`}</span>
    </div>
    <div
      className="h-2 overflow-hidden rounded-full bg-surface-2"
      role="progressbar"
      aria-valuenow={percent}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={t('warehouse.cardFill')}
    >
      <div
        className={cn(
          'h-full rounded-full transition-[width] duration-500',
          percent >= 90 ? 'bg-fill-high' : percent >= 50 ? 'bg-fill-mid' : 'bg-fill-low',
        )}
        style={{ width: `${Math.min(100, percent)}%` }}
      />
    </div>
  </div>
);

/** Bitta raqam va uning nomi — qatorda yonma-yon turadi. */
const Stat = ({ label, value, hint }: { label: string; value: string; hint?: string }) => (
  <div className="flex min-w-0 flex-col gap-0.5 rounded-[var(--radius-md)] border border-border bg-surface p-3">
    <span className="text-xs text-text-faint">{label}</span>
    <span className="tabular text-lg font-semibold text-text">{value}</span>
    {hint === undefined ? null : <span className="truncate text-xs text-text-faint">{hint}</span>}
  </div>
);

/**
 * Partiya kartochkasi.
 *
 * Birinchi qatorda MAHSULOT NOMI turadi, partiya raqami emas: javon oldidagi
 * odam "P-2026-000041" ni emas, "Rels nakladkasi" ni qidiradi. Raqam ikkinchi
 * darajali — u faqat hujjat bilan solishtirishda kerak bo'ladi.
 */
const BatchRow = ({
  place,
  onShowHistory,
  onMove,
  onPrintLabel,
}: {
  place: StockPlaceDto;
  onShowHistory: () => void;
  /** Berilmasa tugma umuman chizilmaydi — ruxsati yo'q xodim uchun. */
  onMove?: () => void;
  /**
   * ETIKETKA BOSISH — qutiga yopishtiriladigani.
   *
   * Aynan shu ekranda, chunki bu yerga javon yorlig'ini skanerlab
   * kelinadi: skladchi javon oldida turadi va qutidagi etiketka
   * yirtilganini o'sha payt ko'radi. Etiketkani bosish uchun boshqa
   * bo'limga borish o'sha lahzani yo'qotardi.
   */
  onPrintLabel?: () => void;
}) => {
  const unit = unitText(place.unit);
  const reserved = place.reservedQty > 0;

  return (
    <li className="flex flex-col gap-2 rounded-[var(--radius-md)] border border-border bg-surface p-3 transition-colors hover:border-border-strong">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="min-w-0 truncate text-sm font-medium text-text">
            {place.nomenclatureName}
          </span>
          <span className="tabular text-xs text-text-faint">{place.nomenclatureCode}</span>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {/* Ko'chirish — XATONI TUZATISH yo'li. Mahsulot noto'g'ri
              katakka tushib qolsa, ilgari uni tizimda tuzatishning yo'li
              yo'q edi: server ko'chirishni bilardi, ekran taklif qilmasdi.
              Faqat band qilinmagan qismi bo'lganda ko'rinadi. */}
          {onMove === undefined || place.availableQty <= 0 ? null : (
            <Button variant="ghost" size="sm" onClick={onMove}>
              <MoveRight size={14} aria-hidden />
              {t('warehouse.cellBatchMove')}
            </Button>
          )}
          {onPrintLabel === undefined ? null : (
            <Button variant="ghost" size="sm" onClick={onPrintLabel}>
              <Tag size={14} aria-hidden />
              {t('warehouse.cellBatchLabel')}
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={onShowHistory}>
            <History size={14} aria-hidden />
            {t('warehouse.cellBatchHistory')}
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <span className="tabular text-sm text-text">
          {t('warehouse.cellBatchAvailableUnit', { qty: place.availableQty, unit })}
        </span>
        {/*
          Band qilingan miqdor faqat NOL BO'LMAGANDA ko'rinadi: har satrda
          turgan "band: 0" ko'zni chalg'itadi va haqiqiy bandni yo'qotadi.
        */}
        {reserved ? (
          <span className="tabular text-sm text-warn">
            {t('warehouse.cellBatchReservedUnit', { qty: place.reservedQty, unit })}
          </span>
        ) : null}
        <span className="tabular text-xs text-text-faint">
          {t('warehouse.cellBatchTotalUnit', { qty: place.qty, unit })}
        </span>
        <span className="tabular ml-auto text-xs text-text-faint">{place.batchNo}</span>
      </div>
    </li>
  );
};

/**
 * KATAK KARTOCHKASI — skanerdan keyingi ekran.
 *
 * Sahifa MANZILDAN yashaydi (`/warehouses/:id/cells/:code`), holatdan emas:
 * skladchi uni xatcho'pga oladi, hamkasbiga havola yuboradi va brauzerning
 * "orqaga" tugmasi kutilganidek ishlaydi.
 *
 * Kod bo'yicha izlanadi, `id` bo'yicha emas: etiketkada AYNAN kod bosilgan
 * va skaner shuni yuboradi. Ombor ham manzilda — bir kod ikki omborda
 * uchraganda qaysi biri ekani havolaning o'zida ko'rinib turadi.
 */
export const CellCardPage = ({ warehouseId, code, onBack, onShowOnLayout }: CellCardPageProps) => {
  const { can } = usePermission();
  const [history, setHistory] = useState<HistoryTarget | null>(null);
  const [moving, setMoving] = useState<MoveStockTarget | null>(null);
  const [printOpen, setPrintOpen] = useState(false);
  /* Etiketka bosiladigan partiya — ro'yxat bo'lib turadi, chunki oyna
     bir nechtasini ham qabul qiladi (kirim hujjatida shunday). */
  const [batchLabel, setBatchLabel] = useState<BatchLabelTarget[] | null>(null);

  const scan = useQuery({
    queryKey: ['warehouse', 'cell-scan', warehouseId, code],
    queryFn: () => warehouseApi.scanCell(code, warehouseId),
    retry: false,
    staleTime: 30_000,
  });

  const cell: CellCardDto | undefined = scan.data?.[0];
  const places = useStockByCell(cell?.id ?? null);
  /* Ko'chirish ombor doirasida tekshiriladi: boshqa omborning katagini
     ochgan xodimga tugma umuman ko'rinmasligi kerak. */
  const canMove = cell !== undefined && can('stock.move', { warehouseId });
  const canLabel = cell !== undefined && can('warehouse.label.print', { warehouseId });

  if (scan.isPending) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton lines={3} />
        <Skeleton lines={6} />
      </div>
    );
  }

  if (scan.isError || cell === undefined) {
    return (
      <EmptyState
        className="rounded-[var(--radius-lg)] border border-border bg-surface"
        icon={<ScanLine size={20} aria-hidden />}
        title={t('warehouse.cellCardNotFound', { code })}
        description={
          scan.error instanceof ApiClientError
            ? scan.error.message
            : t('warehouse.cellCardNotFoundHint')
        }
        action={
          <Button variant="secondary" onClick={onBack}>
            <ArrowLeft size={16} aria-hidden />
            {t('warehouse.back')}
          </Button>
        }
      />
    );
  }

  const rows = places.data ?? [];
  const totalQty = rows.reduce((sum, place) => sum + place.qty, 0);
  const reservedQty = rows.reduce((sum, place) => sum + place.reservedQty, 0);
  const availableQty = rows.reduce((sum, place) => sum + place.availableQty, 0);

  /* Sig'im o'lchanmagan bo'lsa chiziq yo'q — yolg'on nol foiz ko'rsatilmaydi. */
  const fillPercent =
    cell.capacityQty === null || cell.capacityQty <= 0
      ? null
      : Math.round((totalQty / cell.capacityQty) * 100);

  const address = [
    cell.warehouseName,
    `${cell.zoneCode} · ${cell.zoneName}`,
    t('warehouse.codeRack', { rack: cell.rackName ?? cell.rackCode }),
    t('warehouse.codeLevel', { level: cell.level }),
    t('warehouse.codeIdx', { idx: cell.idx }),
  ].join(' · ');

  return (
    <div className="flex flex-col gap-4">
      {/*
        SARLAVHA — ekranning yarmini kod egallaydi va bu ataylab: odam
        etiketkani skanerlab, ekranda AYNAN o'sha kodni ko'rishi kerak.
        Mos kelmasa (skaner boshqasini o'qigan bo'lsa) darhol bilinadi.
      */}
      <header className="relative overflow-hidden rounded-[var(--radius-lg)] border border-border bg-surface p-5">
        {/* Bezak: burchakdagi yumshoq nur. `aria-hidden` — ma'no tashimaydi. */}
        <div
          aria-hidden
          className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-accent-soft blur-2xl"
        />

        <div className="relative flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="ghost" size="sm" onClick={onBack}>
              <ArrowLeft size={16} aria-hidden />
              {t('warehouse.back')}
            </Button>
            <Badge tone={cellStatusTone(cell.status)}>{cellStatusLabel(cell.status)}</Badge>
            <span className="ml-auto inline-flex items-center gap-1.5 text-xs text-text-faint">
              <ScanLine size={14} aria-hidden />
              {t('warehouse.cellCardScanned')}
            </span>
          </div>

          <div className="flex flex-col gap-1">
            <h1 className="tabular text-3xl font-semibold tracking-tight text-text sm:text-4xl">
              {cell.code}
            </h1>
            <p className="text-sm text-text-muted">{address}</p>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" size="sm" onClick={() => onShowOnLayout(cell.id)}>
              <Layers size={16} aria-hidden />
              {t('warehouse.cellCardOnLayout')}
            </Button>
            {can('warehouse.label.print', { warehouseId }) ? (
              <Button variant="secondary" size="sm" onClick={() => setPrintOpen(true)}>
                <Printer size={16} aria-hidden />
                {t('warehouse.labelPrint')}
              </Button>
            ) : null}
          </div>
        </div>
      </header>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label={t('warehouse.cellCardBatches')} value={String(rows.length)} />
        <Stat label={t('warehouse.cellCardTotalQty')} value={String(totalQty)} />
        <Stat label={t('warehouse.cellCardAvailableQty')} value={String(availableQty)} />
        <Stat
          label={t('warehouse.cellCardReservedQty')}
          value={String(reservedQty)}
          {...(reservedQty > 0 ? { hint: t('warehouse.cellCardReservedHint') } : {})}
        />
      </div>

      {/*
        Sig'im va izoh — kartochkaning "hujjat" qismi. Ikkalasi ham bo'lmasa
        bo'lim umuman chizilmaydi: bo'sh ramka ekranni behuda cho'zadi.
      */}
      {fillPercent === null && cell.capacityWeightKg === null && cell.note === null ? null : (
        <section className="flex flex-col gap-3 rounded-[var(--radius-lg)] border border-border bg-surface p-4">
          {fillPercent === null ? null : <FillBar percent={fillPercent} />}
          <dl className="grid gap-3 sm:grid-cols-2">
            {cell.capacityQty === null ? null : (
              <div className="flex flex-col gap-0.5">
                <dt className="text-xs text-text-faint">{t('warehouse.cellCapacityQty')}</dt>
                <dd className="tabular text-sm text-text">{cell.capacityQty}</dd>
              </div>
            )}
            {cell.capacityWeightKg === null ? null : (
              <div className="flex flex-col gap-0.5">
                <dt className="text-xs text-text-faint">{t('warehouse.cellCapacityWeight')}</dt>
                <dd className="tabular text-sm text-text">{cell.capacityWeightKg}</dd>
              </div>
            )}
          </dl>
          {cell.note === null ? null : (
            <p className="rounded-[var(--radius-sm)] border border-dashed border-border p-3 text-sm text-text-muted">
              {cell.note}
            </p>
          )}
        </section>
      )}

      <section className="flex flex-col gap-3">
        <h2 className="flex items-center gap-2 text-sm font-medium text-text">
          <Boxes size={16} aria-hidden />
          {t('warehouse.cellBatchesTitle')}
        </h2>

        {places.isPending ? (
          <Skeleton lines={4} />
        ) : rows.length === 0 ? (
          <EmptyState
            className="rounded-[var(--radius-lg)] border border-dashed border-border bg-surface"
            icon={<Tag size={20} aria-hidden />}
            title={t('warehouse.cellBatchesEmpty')}
            description={t('warehouse.cellCardEmptyHint')}
          />
        ) : (
          <ul className="flex flex-col gap-2">
            {rows.map((place) => (
              <BatchRow
                key={place.batchId}
                place={place}
                onShowHistory={() =>
                  setHistory({ batchId: place.batchId, batchNo: place.batchNo, unit: place.unit })
                }
                {...(canLabel
                  ? {
                      onPrintLabel: () =>
                        setBatchLabel([
                          {
                            batchId: place.batchId,
                            batchNo: place.batchNo,
                            name: place.nomenclatureName,
                            hint: `${String(place.qty)} ${unitText(place.unit)}`,
                          },
                        ]),
                    }
                  : {})}
                {...(canMove
                  ? {
                      onMove: () =>
                        setMoving({
                          batchId: place.batchId,
                          batchNo: place.batchNo,
                          nomenclatureId: place.nomenclatureId,
                          nomenclatureName: place.nomenclatureName,
                          unit: place.unit,
                          qty: place.qty,
                          availableQty: place.availableQty,
                        }),
                    }
                  : {})}
              />
            ))}
          </ul>
        )}
      </section>

      <BatchHistoryDialog
        open={history !== null}
        onClose={() => setHistory(null)}
        batchId={history?.batchId ?? null}
        batchNo={history?.batchNo ?? ''}
        unit={history?.unit ?? null}
      />

      <MoveStockDialog
        open={moving !== null}
        onClose={() => setMoving(null)}
        warehouseId={warehouseId}
        fromCellId={cell.id}
        fromCellCode={cell.code}
        target={moving}
        /* Ro'yxat SERVERDAN qayta o'qiladi: ko'chirishdan keyin katakda
           nima qolganini taxmin qilish emas, ko'rish kerak. */
        onMoved={() => void places.refetch()}
      />

      <LabelPrintDialog
        open={printOpen}
        onClose={() => setPrintOpen(false)}
        cellIds={[cell.id]}
      />

      <BatchLabelDialog
        open={batchLabel !== null}
        onClose={() => setBatchLabel(null)}
        targets={batchLabel ?? []}
      />
    </div>
  );
};
